// src/utils/branding/applyBranding.ts
/**
 * @file Runtime application of a clinic's branding to the live document (S7, ClickUp 86d3w1g8u).
 * @module utils/branding/applyBranding
 *
 * There is ONE shared `mygene.app` image for every tenant (decision D2), so the browser tab
 * title, the favicon and the primary colour cannot be baked in at build time. `index.html`
 * ships `<title>Xoe Med</title>` and `<link rel="icon" href="/xoe-med-mark.svg">`; this module
 * rewrites both, and the primary ramp, at runtime once branding resolves.
 *
 * *** THE ORDERING IS THE WHOLE POINT OF THIS FILE. ***
 *
 * `applyFullTheme()` → `applyCSSColorGroup()` writes the same seven `--mg-primary*` custom
 * properties inline on `document.documentElement` — on boot AND on every preference change
 * (`useUserPreferences.tsx` ×4, `App.tsx`'s `themeChange` handler). Precedence is decided
 * purely by who writes last, so a brand override applied once at startup is SILENTLY REVERTED
 * the first time a user touches the accent picker. {@link reapplyBrandColor} therefore has to
 * be called immediately after EVERY `applyFullTheme` await, including both error fallbacks.
 *
 * ⚠️ Do NOT "fix" this by threading the colour through `applyFullTheme()` as a parameter. It
 * is called from two files with different data in scope, and its own `catch` branch resets to
 * `('dark', 'blue')` — a parameter would have to be re-plumbed through that too. Re-applying
 * after the await is simpler and covers the error path for free.
 *
 * *** DECISION D6 — THE CLINIC COLOUR REPLACES THE PRIMARY RAMP ONLY. ***
 *
 * `--mg-success`, `--mg-warning`, `--mg-danger`, `--mg-info` and the tag palette keep coming
 * from the user's `accentColor` group. A clinic supplies one hex; generating a whole
 * harmonised 30-token palette from it is a different, much larger job.
 */

import { hexToRgb } from '../../design-system/colorGroups';
import { Branding, DEFAULT_BRANDING } from '../../types/branding/BrandingTypes';

/** What `index.html` ships, and what a non-whitelabelled clinic must keep. */
export const DEFAULT_DOCUMENT_TITLE = 'Xoe Med';
export const DEFAULT_FAVICON_HREF = '/xoe-med-mark.svg';
export const DEFAULT_FAVICON_TYPE = 'image/svg+xml';
/** Clinic assets are PNG / JPG / WEBP / ICO — never SVG (decision D10). */
const CLINIC_FAVICON_TYPE = 'image/png';

/**
 * A six-digit hex, the only shape the ramp derivation is defined for.
 *
 * ⚠️ This is DEFENCE IN DEPTH, not the gate. S4's server-side `/^#[0-9a-fA-F]{6}$/` is what
 * makes it safe to put a clinic-supplied string into a CSS custom property — do not relax that
 * and do not treat this line as a substitute for it. Its job here is narrower: keep a value
 * that somehow got past the server (or a hand-edited cache) from producing `NaN` shades.
 */
const BRAND_HEX = /^#[0-9a-fA-F]{6}$/;

/** The seven properties `applyCSSColorGroup` writes for the primary ramp — and no others. */
export const BRAND_RAMP_PROPERTIES = [
	'--mg-primary',
	'--mg-primary-hover',
	'--mg-primary-active',
	'--mg-primary-bg',
	'--mg-primary-bg-hover',
	'--mg-primary-border',
	'--mg-primary-border-hover',
] as const;

export const isBrandHex = (value: unknown): value is string =>
	typeof value === 'string' && BRAND_HEX.test(value);

/**
 * Channel triple for a validated six-digit hex.
 *
 * ⚠️ Parsed out of the EXISTING {@link hexToRgb} rather than re-implemented. That helper is the
 * one the colour registry itself uses to build its `rgba()` strings, and a second hex parser in
 * the codebase is exactly how a clinic's tint ends up one rounding step away from a built-in
 * accent's.
 */
const channels = (hex: string): [number, number, number] => {
	const [r, g, b] = hexToRgb(hex)
		.split(',')
		.map((part) => parseInt(part.trim(), 10));
	return [r, g, b];
};

const clamp = (n: number): number => Math.max(0, Math.min(255, Math.round(n)));

const toHex = (r: number, g: number, b: number): string =>
	`#${[r, g, b].map((c) => clamp(c).toString(16).padStart(2, '0')).join('')}`;

/** Mix towards white. `#ffffff` stays `#ffffff` — there is nothing lighter. */
const lighten = (hex: string, amount: number): string => {
	const [r, g, b] = channels(hex);
	return toHex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount);
};

/** Mix towards black. `#000000` stays `#000000` — there is nothing darker. */
const darken = (hex: string, amount: number): string => {
	const [r, g, b] = channels(hex);
	return toHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
};

/**
 * The seven `--mg-primary*` values, derived from one clinic hex.
 *
 * The alpha steps (0.08 / 0.14 / 0.35 / 0.6) are copied verbatim from the built-in groups in
 * `colorGroups.ts`, so a clinic colour composes with the rest of the system — active nav
 * backgrounds, "selected" borders, tag tints — identically to a shipped accent.
 *
 * `--mg-primary` is the hex EXACTLY AS GIVEN, case included: it is what the clinic typed and
 * what the settings preview shows them.
 *
 * @param hex a six-digit `#rrggbb`. Callers guard with {@link isBrandHex}; three-digit shorthand
 *            is rejected upstream (S4 server-side, and {@link applyBrandColor} here).
 */
export const deriveBrandRamp = (hex: string): Record<string, string> => {
	const rgb = hexToRgb(hex);
	return {
		'--mg-primary': hex,
		'--mg-primary-hover': lighten(hex, 0.12),
		'--mg-primary-active': darken(hex, 0.12),
		'--mg-primary-bg': `rgba(${rgb}, 0.08)`,
		'--mg-primary-bg-hover': `rgba(${rgb}, 0.14)`,
		'--mg-primary-border': `rgba(${rgb}, 0.35)`,
		'--mg-primary-border-hover': `rgba(${rgb}, 0.6)`,
	};
};

/**
 * Write the derived ramp inline on `:root` — the same mechanism `applyCSSColorGroup` uses, so
 * precedence is decided purely by who writes last.
 *
 * @returns whether anything was written. `false` for a malformed hex, so a bad value leaves the
 *          user's accent ramp intact rather than half-overwriting it.
 */
export const applyBrandColor = (hex: string): boolean => {
	if (!isBrandHex(hex)) return false;
	const root = document.documentElement;
	Object.entries(deriveBrandRamp(hex)).forEach(([k, v]) => root.style.setProperty(k, v));
	return true;
};

// ── The module-level mirror of the resolved branding ─────────────────────────────────────────

/**
 * ⚠️ `BrandingContext` is the SINGLE SOURCE OF TRUTH; this is a one-writer mirror of it.
 *
 * It exists because the four `applyFullTheme` call sites that have to be followed by a
 * re-apply are NOT reachable from the context: `PreferencesInitializer` is mounted above
 * `<BrandingProvider>`, `ThemedApp` renders it, and `loadTheme` runs from a `window`
 * event listener with no React scope at all. {@link setActiveBranding} is called by
 * `BrandingContext` and by nothing else — never write to it from a component.
 */
let activeBranding: Branding = DEFAULT_BRANDING;

/** @internal Called by `BrandingContext` only. */
export const setActiveBranding = (branding: Branding): void => {
	activeBranding = branding;
};

/** The branding the appliers are currently working from. Test seam; prefer `useBranding()`. */
export const getActiveBranding = (): Branding => activeBranding;

/** @internal Test seam — resets the mirror between cases. */
export const resetActiveBranding = (): void => {
	activeBranding = DEFAULT_BRANDING;
};

/**
 * Re-assert the clinic's primary ramp over whatever `applyFullTheme` just wrote.
 *
 * *** CALL THIS IMMEDIATELY AFTER EVERY `applyFullTheme` AWAIT — ALL OF THEM. ***
 * `useUserPreferences.tsx` ×4 (both success paths and both error fallbacks) and `App.tsx`'s
 * `themeChange` handler. Miss one and the clinic's colour survives boot but not the first
 * accent change, which is the failure this whole subtask exists to prevent.
 *
 * A no-op for a non-whitelabelled clinic and for a whitelabelled clinic that supplied no
 * colour — colour is independently optional from logo and name, so those users keep their own
 * accent. Seven `setProperty` calls, idempotent and cheap; no dirty check needed.
 */
export const reapplyBrandColor = (): void => {
	const { whitelabel, primary_color } = activeBranding;
	if (!whitelabel || !isBrandHex(primary_color)) return;
	applyBrandColor(primary_color);
};

/**
 * Point the browser tab at the clinic: its name in the title, its favicon in the tab.
 *
 * `manifest.json` is deliberately left alone — it is the installed-PWA identity, changes
 * nothing visible in a normal tab, and rewriting it at runtime is not worth the complexity.
 * Known limitation, not a defect: an installed PWA still says Xoe Med.
 */
export const applyDocumentBranding = (branding: Branding): void => {
	const branded = branding.whitelabel;

	document.title = branded && branding.display_name ? branding.display_name : DEFAULT_DOCUMENT_TITLE;

	let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
	if (!link) {
		link = document.createElement('link');
		link.setAttribute('rel', 'icon');
		document.head.appendChild(link);
	}

	const href = branded && branding.favicon_url ? branding.favicon_url : DEFAULT_FAVICON_HREF;
	link.setAttribute('href', href);
	// Clinic assets are served as PNG/ICO (D10 bans SVG); only the bundled default is an SVG.
	link.setAttribute('type', href === DEFAULT_FAVICON_HREF ? DEFAULT_FAVICON_TYPE : CLINIC_FAVICON_TYPE);
};

/**
 * The single entry point: record the resolved branding and apply everything it drives.
 *
 * Called from `BrandingContext` once the fetch settles. Safe to call repeatedly — S9's settings
 * tab calls `refresh()` after a save and the app re-brands with no reload.
 */
export const applyBranding = (branding: Branding): void => {
	setActiveBranding(branding);
	applyDocumentBranding(branding);
	reapplyBrandColor();
};
